/**
 * Currency answer type — amount parsing and display formatting.
 *
 * DISPLAY IS SEPARATE FROM STORAGE. The field shows `100,000.50` behind a `$`
 * prefix; the answer that is stored is the plain decimal string `100000.50`.
 * That matches how the other simple inputs already persist (SimpleInputWidget
 * commits trimmed strings, and percentages are stored as strings), so Currency
 * introduces no new storage contract.
 *
 * FORMATTING HAPPENS ON BLUR ONLY. Reformatting while the user types would need
 * caret tracking and paste interception; formatting once on commit keeps
 * ordinary typing, deletion and paste working with no extra machinery.
 *
 * Grouping reuses `@pofnor/number-comma-formatter`, the same dependency
 * `Mixins/common.js` uses for `formatNumberWithComma` / `formatNumberWithUsdIcon`.
 */
import numberCommaFormatter from "@pofnor/number-comma-formatter";

/**
 * Decimal places a stored amount may carry.
 *
 * There is no existing repository policy for money precision, so excess
 * decimals are REJECTED rather than rounded: silently turning 100.005 into
 * 100.00 would change the amount the user entered.
 */
export const CURRENCY_DECIMAL_PLACES = 2;

/**
 * Accepted syntax, checked before anything is removed: an optional sign, an
 * optional single `$`, digits either plainly or in three-digit groups, and an
 * optional fraction. Validating the original text is what stops `1$2` and
 * `1,00,0` being repaired into 12 and 1000.
 */
const AMOUNT_RE = /^\s*(?:(?<sign>[+-])\s*)?(?:\$\s*)?(?<int>\d{1,3}(?:,\d{3})*|\d+)?(?:\.(?<frac>\d+))?\s*$/;

export function isBlankAmount(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

/**
 * Parse an entered amount to the plain decimal string that gets stored.
 *
 * Returns `""` for blank (so a caller can tell "nothing entered" from
 * "unusable"), or `null` when the text is not a valid amount. Never
 * digit-strips: `12abc` is invalid rather than becoming 12.
 */
export function parseCurrencyAmount(value) {
  if (isBlankAmount(value)) return "";

  const match = AMOUNT_RE.exec(String(value));
  if (!match) return null;

  const { sign, int: integer, frac } = match.groups;
  if (integer === undefined && frac === undefined) return null;
  if (frac !== undefined && frac.length > CURRENCY_DECIMAL_PLACES) return null;

  const whole = (integer || "").replaceAll(",", "") || "0";
  const amount = `${sign === "-" ? "-" : ""}${whole}${frac === undefined ? "" : `.${frac}`}`;
  return Number.isFinite(Number(amount)) ? amount : null;
}

export function isCurrencyAmount(value) {
  return parseCurrencyAmount(value) !== null;
}

/**
 * Format a stored amount for the field: `100,000.50`.
 *
 * Two decimals always, because a money field reading `100,000.5` looks like a
 * typo. The `$` is supplied by the field's prefix, not by this string.
 * `numberCommaFormatter` drops a leading `-`, so the sign is reapplied here.
 */
export function formatCurrencyAmount(value) {
  const parsed = parseCurrencyAmount(value);
  if (parsed === null || parsed === "") return "";

  const negative = parsed.startsWith("-");
  const [whole, fraction = ""] = parsed.replace(/^[+-]/, "").split(".");
  const cents = `${fraction}00`.slice(0, CURRENCY_DECIMAL_PLACES);
  return `${negative ? "-" : ""}${numberCommaFormatter(whole)}.${cents}`;
}

/* ------------------------------------------------------------------ */
/* Input restriction                                                   */
/* ------------------------------------------------------------------ */

/**
 * The shape a currency field is allowed to hold WHILE BEING EDITED.
 *
 * Looser than {@link parseCurrencyAmount} on purpose: it must accept the
 * half-finished states typing goes through — "", "-", "123", "123." — and it
 * must tolerate the thousands separators blur leaves behind, so returning to a
 * formatted "100,000.50" and editing it still works.
 *
 * It does NOT accept letters, a second decimal point, or a third decimal
 * place. Blocking that keystroke is an input restriction, not truncation: the
 * amount already in the field is never rewritten.
 */
export const CURRENCY_DRAFT_RE = /^-?[\d,]*(?:\.\d{0,2})?$/;

export function isCurrencyDraft(text) {
  return CURRENCY_DRAFT_RE.test(String(text ?? ""));
}

/** The value an input would hold if `insert` replaced [start, end). */
export function applyTextEdit(current, insert, start, end) {
  const text = String(current ?? "");
  const from = Number.isInteger(start) ? start : text.length;
  const to = Number.isInteger(end) ? end : from;
  return text.slice(0, from) + String(insert ?? "") + text.slice(to);
}

/**
 * Keypress guard for a currency field.
 *
 * Prevents a keystroke only when it would leave the field in a shape currency
 * can never reach. Everything that is not a single printable character is left
 * alone, which is what keeps Backspace, Delete, Tab, Enter, arrows and
 * selection working, and modifier chords (Ctrl/Cmd+A, +C, +V, +Z) are ignored
 * outright so shortcuts are never swallowed.
 *
 * Returns true when the keystroke was allowed.
 */
export function guardCurrencyKeypress(event) {
  if (!event || event.ctrlKey || event.metaKey || event.altKey) return true;

  const key = event.key;
  // Non-printable keys (Backspace, Delete, ArrowLeft, Enter, Tab, ...).
  if (typeof key !== "string" || [...key].length !== 1) return true;

  const target = event.target || {};
  const next = applyTextEdit(target.value, key, target.selectionStart, target.selectionEnd);
  if (isCurrencyDraft(next)) return true;

  event.preventDefault();
  return false;
}

/**
 * Paste handler for a currency field.
 *
 * Always takes over the paste, then decides:
 *   - the result is a valid amount -> returns it as a plain decimal string
 *     ("$100,000.50" pasted becomes "100000.50"; blur re-adds the separators)
 *   - anything else -> returns null and the caller leaves the field untouched,
 *     so invalid content neither lands in the field nor is stripped down into
 *     some other number
 */
export function currencyPasteAmount(event) {
  if (!event) return null;
  if (event.preventDefault) event.preventDefault();

  const pasted = event.clipboardData ? event.clipboardData.getData("text") : "";
  const target = event.target || {};
  const next = applyTextEdit(target.value, pasted, target.selectionStart, target.selectionEnd);

  const parsed = parseCurrencyAmount(next);
  return parsed === null || parsed === "" ? null : parsed;
}
